import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import {
  Box,
  Button,
  Stack,
  Typography,
} from '@mui/material';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';
import { CustomFilterV2, EmptyState } from '../../../components/v2';
import * as Roles from '../../../constants/role';
import { getRole } from '../../../store/auth/reducer';
import { loadMapaCompleto, cleanMapaCompleto } from '../../../store/nsirMapa/actions';
import {
  getNsirsForMapa,
  getNsirMapaLoadingNsirs,
} from '../../../store/nsirMapa/reducer';
import MapErrorBoundary from './components/MapErrorBoundary';
import NsirMapCanvas from './components/NsirMapCanvas';
import NsirMapaGpsDialog from './components/NsirMapaGpsDialog';
import NsirMapaKpiStrip from './components/NsirMapaKpiStrip';
import NsirMapaPresets from './components/NsirMapaPresets';
import NsirMapaActiveFilters from './components/NsirMapaActiveFilters';
import NsirMapaDrawer from './components/NsirMapaDrawer';
import NsirMapaDetailDialog from './components/NsirMapaDetailDialog';
import {
  buildApiFilter,
  DEFAULT_STATUS_OPTIONS,
  EMPTY_FILTER,
  KPI_KEYS,
  MAP_ROW_HEIGHT,
  MAP_ROW_MIN_HEIGHT,
  PRESET_KEYS,
} from './constants';
import { computeMapMetrics } from './mapMetrics';
import { getDisplayNsirs, shouldFitBounds } from './mapViewFilters';
import { parseNsirMapaSearch, useNsirMapaUrlState } from './useNsirMapaUrlState';

const FILTER_FIELDS = [
  { field: 'useMalha', md: 12, multiSelect: true },
  { field: 'useMunicipio', md: 12, multiSelect: true },
  {
    field: 'useNsirStatus',
    md: 12,
    multiSelect: true,
    filterKey: 'status',
    customStatus: DEFAULT_STATUS_OPTIONS,
  },
];

const NsirMapaV2 = ({
  nsirs,
  loadingNsirs,
  userRole,
  loadMapaCompleto,
  cleanMapaCompleto,
  history,
  location,
}) => {
  const isAdmin = [Roles.ADMIN, Roles.SUPER_ADMIN].includes(userRole);
  const [localFilter, setLocalFilter] = useState(
    () => parseNsirMapaSearch(location.search).filter,
  );
  const [activeKpi, setActiveKpi] = useState(
    () => parseNsirMapaSearch(location.search).activeKpi,
  );
  const [activePreset, setActivePreset] = useState(
    () => parseNsirMapaSearch(location.search).activePreset,
  );
  const [openFilter, setOpenFilter] = useState(false);
  const [openGpsDialog, setOpenGpsDialog] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerNsirs, setDrawerNsirs] = useState([]);
  const [drawerTitle, setDrawerTitle] = useState('NSIRs na área');
  const [viewportNsirs, setViewportNsirs] = useState([]);
  const [selectedNsir, setSelectedNsir] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [highlightNsirId, setHighlightNsirId] = useState(null);
  const [focusNsirKey, setFocusNsirKey] = useState(0);
  const [fitBoundsKey, setFitBoundsKey] = useState(0);

  const bumpFitBounds = useCallback(() => {
    setFitBoundsKey((k) => k + 1);
  }, []);

  const handleExternalUrlChange = useCallback((parsed) => {
    setLocalFilter(parsed.filter);
    setActiveKpi(parsed.activeKpi);
    setActivePreset(parsed.activePreset);
    loadMapaCompleto(buildApiFilter(parsed.filter));
  }, [loadMapaCompleto]);

  useNsirMapaUrlState({
    history,
    location,
    localFilter,
    activeKpi,
    activePreset,
    onExternalUrlChange: handleExternalUrlChange,
  });

  useEffect(() => {
    if (!isAdmin) return undefined;
    loadMapaCompleto(buildApiFilter(localFilter));
    return () => cleanMapaCompleto();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const { metrics, invalidas: nsirsInvalidas } = useMemo(
    () => computeMapMetrics(nsirs),
    [nsirs],
  );

  const displayNsirs = useMemo(
    () => getDisplayNsirs(nsirs, { activeKpi, activePreset }),
    [nsirs, activeKpi, activePreset],
  );

  const handleFilterApply = useCallback(
    (filterData) => {
      setLocalFilter(filterData);
      setOpenFilter(false);
      loadMapaCompleto(buildApiFilter(filterData));
    },
    [loadMapaCompleto],
  );

  const handleSelectKpi = useCallback(
    (kpi) => {
      if (kpi === KPI_KEYS.GPS_ERRORS) {
        setOpenGpsDialog(true);
        return;
      }
      setActiveKpi(kpi);
      if (kpi != null) {
        setActivePreset(null);
        if (shouldFitBounds(kpi, null)) bumpFitBounds();
      }
    },
    [bumpFitBounds],
  );

  const handleSelectPreset = useCallback(
    (preset) => {
      if (preset === PRESET_KEYS.GPS) {
        setOpenGpsDialog(true);
        setActivePreset(preset);
        return;
      }
      if (preset === PRESET_KEYS.COMPLETO) {
        setActivePreset(null);
        setActiveKpi(null);
        bumpFitBounds();
        return;
      }
      setActivePreset(preset);
      setActiveKpi(null);
      if (shouldFitBounds(null, preset)) bumpFitBounds();
    },
    [bumpFitBounds],
  );

  const focusNsirOnMap = useCallback((nsir) => {
    setHighlightNsirId(nsir.id);
    setFocusNsirKey((key) => key + 1);
  }, []);

  const handleMarkerClick = useCallback((nsir) => {
    setHighlightNsirId(nsir.id);
    setSelectedNsir(nsir);
    setDetailOpen(true);
  }, []);

  const handleClusterClick = useCallback((clusterNsirs) => {
    setDrawerNsirs(clusterNsirs);
    setDrawerTitle(`Cluster (${clusterNsirs.length} NSIRs)`);
    setDrawerOpen(true);
  }, []);

  const handleViewportChange = useCallback((inViewport) => {
    setViewportNsirs(inViewport);
    if (!drawerOpen) {
      setDrawerNsirs(inViewport);
      setDrawerTitle('NSIRs na área visível');
    }
  }, [drawerOpen]);

  const handleDrawerSelectNsir = focusNsirOnMap;

  const handleToggleDrawer = useCallback(() => {
    setDrawerOpen((open) => {
      const next = !open;
      if (next && drawerNsirs.length === 0) {
        setDrawerNsirs(viewportNsirs);
        setDrawerTitle('NSIRs na área visível');
      }
      return next;
    });
  }, [drawerNsirs.length, viewportNsirs]);

  const listNsirs = drawerOpen ? drawerNsirs : viewportNsirs;

  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <EmptyState
          title="Acesso restrito"
          message="Apenas administradores podem visualizar o mapa de NSIRs."
        />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack spacing={2}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}
        >
          <Box>
            <Typography variant="h4">Georreferenciamento de NSIRs</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              Painel operacional — visualize anomalias, backlog e cobertura geográfica
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            startIcon={<FilterListOutlinedIcon />}
            onClick={() => setOpenFilter(true)}
          >
            Filtros
          </Button>
        </Stack>

        <NsirMapaActiveFilters
          localFilter={localFilter}
          activeKpi={activeKpi}
          activePreset={activePreset}
          onClearKpi={() => setActiveKpi(null)}
          onClearPreset={() => setActivePreset(null)}
        />

        <NsirMapaKpiStrip
          metrics={metrics}
          loading={loadingNsirs}
          activeKpi={activeKpi}
          onSelectKpi={handleSelectKpi}
        />

        <NsirMapaPresets
          activePreset={activePreset}
          onSelectPreset={handleSelectPreset}
        />

        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={0}
          alignItems="stretch"
          sx={{
            height: MAP_ROW_HEIGHT,
            minHeight: MAP_ROW_MIN_HEIGHT,
            borderRadius: 1,
            overflow: 'hidden',
            border: 1,
            borderColor: 'divider',
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <MapErrorBoundary>
              <NsirMapCanvas
                displayNsirs={displayNsirs}
                loading={loadingNsirs}
                fitBoundsKey={fitBoundsKey}
                focusNsirId={highlightNsirId}
                focusNsirKey={focusNsirKey}
                highlightNsirId={highlightNsirId}
                drawerOpen={drawerOpen}
                viewportCount={viewportNsirs.length}
                onMarkerClick={handleMarkerClick}
                onClusterClick={handleClusterClick}
                onViewportChange={handleViewportChange}
                onToggleDrawer={handleToggleDrawer}
              />
            </MapErrorBoundary>
          </Box>

          {drawerOpen && (
            <NsirMapaDrawer
              onClose={() => setDrawerOpen(false)}
              nsirs={listNsirs}
              title={drawerTitle}
              highlightNsirId={highlightNsirId}
              onSelectNsir={handleDrawerSelectNsir}
            />
          )}
        </Stack>
      </Stack>

      <NsirMapaGpsDialog
        open={openGpsDialog}
        onClose={() => setOpenGpsDialog(false)}
        nsirsInvalidas={nsirsInvalidas}
      />

      <NsirMapaDetailDialog
        open={detailOpen}
        nsir={selectedNsir}
        onClose={() => {
          setDetailOpen(false);
          setSelectedNsir(null);
        }}
      />

      <CustomFilterV2
        variant="dialog"
        open={openFilter}
        onClose={() => setOpenFilter(false)}
        title="Filtros do mapa"
        fullWidth
        appliedFilter={localFilter}
        fields={FILTER_FIELDS}
        onFilter={handleFilterApply}
      />
    </Box>
  );
};

NsirMapaV2.propTypes = {
  nsirs: PropTypes.array.isRequired,
  loadingNsirs: PropTypes.bool.isRequired,
  userRole: PropTypes.string,
  loadMapaCompleto: PropTypes.func.isRequired,
  cleanMapaCompleto: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  nsirs: getNsirsForMapa(state),
  loadingNsirs: getNsirMapaLoadingNsirs(state),
  userRole: getRole(state),
});

const mapDispatchToProps = {
  loadMapaCompleto,
  cleanMapaCompleto,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withRouter(NsirMapaV2));
